import axios from "axios";
import {
  ACCESS_TOKEN_SESSION_KEY,
  ID_TOKEN_SESSION_KEY,
  REFRESH_TOKEN_SESSION_KEY,
  COGNITO_CLIENT_ID,
  LOGIN_URL,
  LOGOUT_URL,
} from "../const";

export type TokenData = {
  id_token: string;
  access_token: string;
};

const TOKEN_ENDPOINT = "https://login.bourland.link/oauth2/token";

// Cognito requires the token-exchange redirect_uri to byte-for-byte match the
// one used in the /authorize request. LOGIN_URL uses window.location.origin, so
// derive it from a single source here to keep them in sync.
const REDIRECT_URI = window.location.origin;

// Loop breaker: if we keep failing to get tokens we must stop bouncing back to
// the IDP, otherwise the user is stuck in an infinite redirect.
const REDIRECT_ATTEMPTS_KEY = "auth_redirect_attempts";
const MAX_REDIRECT_ATTEMPTS = 2;

export class AuthError extends Error {
  constructor(
    message: string,
    public readonly cause?: unknown,
  ) {
    super(message);
    this.name = "AuthError";
  }
}

const clearSession = () => {
  sessionStorage.removeItem(ID_TOKEN_SESSION_KEY);
  sessionStorage.removeItem(ACCESS_TOKEN_SESSION_KEY);
  sessionStorage.removeItem(REFRESH_TOKEN_SESSION_KEY);
};

// Redirect to the IDP, but only if we haven't already tried too many times.
// Returns true if it redirected, false if the loop breaker tripped.
const redirectToLogin = (): boolean => {
  const attempts =
    Number(sessionStorage.getItem(REDIRECT_ATTEMPTS_KEY) ?? "0") || 0;
  if (attempts >= MAX_REDIRECT_ATTEMPTS) {
    // Give up rather than loop forever. Surface the failure to the caller.
    return false;
  }
  sessionStorage.setItem(REDIRECT_ATTEMPTS_KEY, String(attempts + 1));
  window.location.assign(LOGIN_URL);
  return true;
};

// Called on any confirmed-good login so the loop breaker resets for next time.
const markLoginSuccess = () => {
  sessionStorage.removeItem(REDIRECT_ATTEMPTS_KEY);
};

// Strip the ?code= (and other oauth params) from the URL so a refresh or a
// second read never tries to reuse a single-use authorization code.
const stripAuthParamsFromUrl = () => {
  window.history.replaceState({}, document.title, "/");
};

export const checkLoginStatus = () => {
  const urlParams = new URLSearchParams(window.location.search);
  return (
    sessionStorage.getItem(REFRESH_TOKEN_SESSION_KEY) !== null ||
    urlParams.has("code")
  );
};

const parseJwt = (jwt: string) => {
  const jwtBody = jwt.split(".")[1];
  return JSON.parse(window.atob(jwtBody));
};

const persistTokens = (data: {
  access_token: string;
  id_token: string;
  refresh_token?: string;
}) => {
  sessionStorage.setItem(ACCESS_TOKEN_SESSION_KEY, data.access_token);
  sessionStorage.setItem(ID_TOKEN_SESSION_KEY, data.id_token);
  if (data.refresh_token) {
    sessionStorage.setItem(REFRESH_TOKEN_SESSION_KEY, data.refresh_token);
  }
};

// Turn a Cognito token error into a short human-readable summary. Cognito
// returns { error: "invalid_grant", ... } on a reused/expired code, which is
// exactly the redirect-loop symptom.
const describeTokenError = (error: unknown): string => {
  if (axios.isAxiosError(error) && error.response?.data) {
    const data = error.response.data as {
      error?: string;
      error_description?: string;
    };
    return data.error_description || data.error || error.message;
  }
  return error instanceof Error ? error.message : String(error);
};

// Build the full diagnostic blob shown in the expandable "Show details"
// section of the error page: HTTP status, Cognito response body, and stack.
const buildErrorDetail = (context: string, error: unknown): string => {
  const parts: string[] = [`Context: ${context}`];
  if (axios.isAxiosError(error)) {
    if (error.response) {
      parts.push(`HTTP ${error.response.status} ${error.response.statusText}`);
      try {
        parts.push(`Response: ${JSON.stringify(error.response.data, null, 2)}`);
      } catch {
        parts.push(`Response: ${String(error.response.data)}`);
      }
    } else {
      parts.push("No HTTP response (network/CORS error).");
    }
    if (error.config?.url) {
      parts.push(`Request URL: ${error.config.url}`);
    }
  }
  if (error instanceof Error && error.stack) {
    parts.push(`Stack:\n${error.stack}`);
  } else {
    parts.push(`Raw: ${String(error)}`);
  }
  return parts.join("\n\n");
};

// Last-resort landing when the loop breaker trips. We redirect (rather than
// throw) because the failure often originates in an un-awaited async callback
// that react-router's errorElement can't catch. Details ride along in the
// query string so the error page can render + expand them.
const redirectToAuthError = (message: string, detail: string): void => {
  const qs = new URLSearchParams({
    auth_error: message,
    auth_detail: detail,
  }).toString();
  window.location.assign(`/auth-error?${qs}`);
};

// A single in-flight token request shared across concurrent callers. Without
// this, getAccessToken() and getIdToken() both fire fetchTokensWithCode() with
// the SAME single-use code; the first consumes it, the second gets
// invalid_grant and triggers the redirect loop.
let inFlight: Promise<TokenData> | null = null;

export const fetchTokensWithCode = async (code: string): Promise<TokenData> => {
  // Remove the code from the URL up front so it can't be reused on reload.
  stripAuthParamsFromUrl();
  try {
    const resultData = await axios.post(
      TOKEN_ENDPOINT,
      new URLSearchParams({
        grant_type: "authorization_code",
        client_id: COGNITO_CLIENT_ID,
        code: code,
        redirect_uri: REDIRECT_URI,
      }).toString(),
      { headers: { "Content-Type": "application/x-www-form-urlencoded" } },
    );
    persistTokens(resultData.data);
    markLoginSuccess();
    return resultData.data as TokenData;
  } catch (error) {
    // The code was bad/expired/reused. Clear it and either retry login once
    // or surface the error so we don't loop forever.
    if (!redirectToLogin()) {
      redirectToAuthError(
        `Token exchange failed: ${describeTokenError(error)}`,
        buildErrorDetail("fetchTokensWithCode (authorization_code grant)", error),
      );
      throw new AuthError(
        `Token exchange failed: ${describeTokenError(error)}`,
        error,
      );
    }
    return { id_token: "", access_token: "" } as TokenData;
  }
};

const doRefresh = async (refreshToken: string): Promise<TokenData> => {
  try {
    const resultData = await axios.post(
      TOKEN_ENDPOINT,
      new URLSearchParams({
        grant_type: "refresh_token",
        client_id: COGNITO_CLIENT_ID,
        refresh_token: refreshToken,
        redirect_uri: REDIRECT_URI,
      }).toString(),
      { headers: { "Content-Type": "application/x-www-form-urlencoded" } },
    );
    persistTokens(resultData.data);
    markLoginSuccess();
    return resultData.data as TokenData;
  } catch (error) {
    // Refresh token is invalid/expired. Clear it so we don't keep retrying a
    // dead token, then fall back to interactive login.
    clearSession();
    if (!redirectToLogin()) {
      redirectToAuthError(
        `Token refresh failed: ${describeTokenError(error)}`,
        buildErrorDetail("doRefresh (refresh_token grant)", error),
      );
      throw new AuthError(
        `Token refresh failed: ${describeTokenError(error)}`,
        error,
      );
    }
    return { id_token: "", access_token: "" } as TokenData;
  }
};

export const refreshTokens = async (): Promise<TokenData> => {
  // Coalesce concurrent callers onto one request.
  if (inFlight) {
    return inFlight;
  }

  const run = async (): Promise<TokenData> => {
    const currRefreshToken = sessionStorage.getItem(REFRESH_TOKEN_SESSION_KEY);
    const urlParams = new URLSearchParams(window.location.search);

    if (currRefreshToken != null) {
      return doRefresh(currRefreshToken);
    }
    if (urlParams.has("code")) {
      return fetchTokensWithCode(urlParams.get("code") as string);
    }
    if (!redirectToLogin()) {
      const message = "Not authenticated and login redirect loop detected.";
      redirectToAuthError(
        message,
        buildErrorDetail(
          "refreshTokens (no refresh token and no authorization code present)",
          new Error(message),
        ),
      );
      throw new AuthError(message);
    }
    return { id_token: "", access_token: "" } as TokenData;
  };

  inFlight = run();
  try {
    return await inFlight;
  } finally {
    inFlight = null;
  }
};

const isExpired = (token: string): boolean => {
  try {
    const claims = parseJwt(token);
    const nowSecs = Date.now() / 1000;
    // 30s clock-skew buffer avoids using a token that's about to expire.
    return claims.exp < nowSecs + 30;
  } catch {
    return true;
  }
};

export const getAccessToken = async () => {
  const currAccessToken = sessionStorage.getItem(ACCESS_TOKEN_SESSION_KEY);
  if (currAccessToken !== null && !isExpired(currAccessToken)) {
    return currAccessToken;
  }
  const refreshResult = await refreshTokens();
  return refreshResult.access_token;
};

export const getIdToken = async () => {
  const currIdToken = sessionStorage.getItem(ID_TOKEN_SESSION_KEY);
  if (currIdToken !== null && !isExpired(currIdToken)) {
    return currIdToken;
  }
  const refreshResult = await refreshTokens();
  return refreshResult.id_token;
};

export const getName = async () => {
  const token = await getIdToken();
  if (!token) {
    return "";
  }
  return parseJwt(token)["cognito:username"] as string;
};

export const logout = () => {
  clearSession();
  sessionStorage.removeItem(REDIRECT_ATTEMPTS_KEY);
  window.location.assign(LOGOUT_URL);
};

// eslint-disable-next-line
export default {
  checkLoginStatus,
  fetchTokensWithCode,
  getAccessToken,
  getIdToken,
  getName,
  logout,
};
